import { Box, TextField, Button, Tooltip } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { useParams } from 'react-router-dom';
import { useState, useContext } from 'react';
import PropTypes from 'prop-types';
import AwaitButton from '../../../../reusable-components/await-button/AwaitButton';
import { useDispatch, useSelector } from '../../../../redux/store';
import { addItemsToCart, setAdditionToCartBadge } from '../../../../redux/slices/orderSheet';
import { ShopCartContext } from './_ShopViewContext';
import { getCartInfoItemQty } from './ordersheetUtils';

AddToCartButton.propTypes = {
  item: PropTypes.object,
  editingItemId: PropTypes.number,
  setEditingItemId: PropTypes.func,
};
export default function AddToCartButton({ item, editingItemId, setEditingItemId }) {

  const { cart, setCart } = useContext(ShopCartContext);
  const dispatch = useDispatch();
  const { facilityId = '' } = useParams();
  const searchParams = new URLSearchParams(window.location.search);
  const categoryId = searchParams.get('categoryId');
  const { data: { cartInfo } } = useSelector((state) => state.orderSheet);
  const [loadingId, setLoadingId] = useState(null);
  const quantity = cart[item.id] ?? item.quantity ?? 0;

  const cartInfoItemQty = getCartInfoItemQty(item, categoryId, cartInfo);

  const inCart = !item.invalidItem && cartInfoItemQty > 0;

  const getProductToAdd = (item) => ({
    ...item,
    agoraCategoryId: item.displayCategory.id,
    agoraSubcategoryId: item.displaySubcategory.id,
  });

  const handleAddCart = (productToAdd) => {
    dispatch(addItemsToCart(productToAdd, facilityId, categoryId))
      .catch(() => enqueueSnackbar('Failed to update cart', { variant: 'error' }))
      .finally(() => setEditingItemId(null));
  };

  const getCartWithDiffs = (productToAdd) => [
      {
        ...productToAdd,
        quantity: productToAdd?.quantity - (cartInfoItemQty || 0),
        uomType: productToAdd.productDetails[0]?.uomType,
      },
  ];

  const itemInCartAndUpdated = cartInfoItemQty && cart[item.id] && cartInfoItemQty !== cart[item.id];

  return (
    <>
      {!inCart || itemInCartAndUpdated || editingItemId === item.id ? (
        <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
          <Tooltip
            arrow
            placement="top"
            title={`${cartInfoItemQty} in cart`}
            disableHoverListener={!itemInCartAndUpdated}
            disableFocusListener={!itemInCartAndUpdated}
            disableTouchListener={!itemInCartAndUpdated}
          >
            <TextField
              type="number"
              size="small"
              value={quantity}
              error={quantity < 0}
              onChange={(e) => {
                const raw = e.target.value;
                if (raw === '') {
                  setCart(c => ({ ...c, [item.id]: '' }));
                  return;
                }
                const value = Number(raw);
                setCart(c => ({ ...c, [item.id]: value }));
              }}
              onKeyDown={(e) => {
                if (e.key === '-' || e.key === 'e' || e.key === '.' || e.key === ',' || e.key === 'ArrowUp' || e.key === 'ArrowDown') e.preventDefault();
              }}
              onWheel={(e) => e.target.blur()}
              sx={{ width: 95, '& input': { textAlign: 'center' } }}
              autoComplete="off"
            />
          </Tooltip>
          <AwaitButton
            variant="contained"
            fullWidth
            loading={loadingId === item.id}
            disabled={quantity < 0}
            onClick={async () => {
              setLoadingId(item.id);

              const productToAdd = getProductToAdd(item);
              const qtyToUse = quantity === '' ? 0 : quantity;

              try {
                const cartWithDiffs = getCartWithDiffs({ ...productToAdd, quantity: qtyToUse });
                await handleAddCart(cartWithDiffs);
                setCart(cart => ({ ...cart, [item.id]: qtyToUse }));
                dispatch(setAdditionToCartBadge(cartWithDiffs.reduce((a, b) => a + b.quantity, 0)));
              } finally {
                setLoadingId(null);
              }
            }}
          >
            Update Cart
          </AwaitButton>
        </Box>
      ) : (
        <Button
          variant="outlined"
          fullWidth
          onClick={(e) => {
            e.stopPropagation();
            setEditingItemId(item.id);
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {quantity} in cart
            <EditOutlinedIcon fontSize="small" />
          </Box>
        </Button>
      )}
    </>
  );
}
